/**
 * AdminSessionsCard — session/IP activity log viewer for super admins.
 * Shows recent login/logout events with IP, user-agent, and session
 * duration (calculated on the logout side).
 *
 * CSV export button uses a fresh session cookie (via backend endpoint
 * `/admin/ops/sessions/export.csv`).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, RefreshCw, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function AdminSessionsCard() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [kind, setKind] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    async function load() {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", "100");
            if (kind) params.set("kind", kind);
            if (email) params.set("email", email);
            const { data } = await api.get(`/admin/ops/sessions?${params.toString()}`);
            setRows(data.rows || []);
            setTotal(data.total_matching || 0);
        } catch (e) {
            toast.error("Failed to load sessions");
        } finally { setBusy(false); }
    }

    useEffect(() => { load(); }, []); // eslint-disable-line

    function exportCsv() {
        const params = new URLSearchParams();
        if (kind) params.set("kind", kind);
        if (email) params.set("email", email);
        const url = `${API_BASE}/api/admin/ops/sessions/export.csv?${params.toString()}`;
        window.open(url, "_blank");
    }

    function fmt(ts) {
        if (!ts) return "—";
        try { return new Date(ts).toLocaleString(); } catch { return ts; }
    }

    return (
        <div className="space-y-3" data-testid="admin-sessions-card">
            <div className="border border-border bg-card rounded p-4">
                <div className="flex items-center flex-wrap gap-2">
                    <div className="overline flex items-center gap-1.5">
                        <Filter className="w-3 h-3" /> Filter
                    </div>
                    <select
                        value={kind} onChange={(e) => setKind(e.target.value)}
                        className="text-xs h-8 border border-border bg-background rounded px-2"
                        data-testid="sessions-kind-filter"
                    >
                        <option value="">All events</option>
                        <option value="login">Logins</option>
                        <option value="logout">Logouts</option>
                    </select>
                    <input
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="Filter by email substring"
                        className="text-xs h-8 border border-border bg-background rounded px-2 min-w-[220px]"
                        data-testid="sessions-email-filter"
                    />
                    <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="sessions-reload">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                        {busy ? "Loading…" : "Reload"}
                    </Button>
                    <div className="flex-1" />
                    <Button size="sm" onClick={exportCsv} data-testid="sessions-export-csv">
                        <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                    </Button>
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">
                    Showing {rows.length} of {total.toLocaleString()} matching events (newest first).
                    IPs and user-agents are captured raw — no external geo lookup
                    (closed-loop policy).
                </div>
            </div>

            <div className="border border-border bg-card rounded overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-border text-[11px] uppercase text-muted-foreground">
                            <th className="text-left p-2">Time</th>
                            <th className="text-left p-2">Event</th>
                            <th className="text-left p-2">Email</th>
                            <th className="text-left p-2">Role</th>
                            <th className="text-left p-2">IP</th>
                            <th className="text-left p-2">Duration</th>
                            <th className="text-left p-2 hidden md:table-cell">User-Agent</th>
                        </tr>
                    </thead>
                    <tbody data-testid="sessions-table-body">
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="p-6 text-center text-muted-foreground text-sm">
                                No session events yet. Log in / out to populate.
                            </td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/40">
                                <td className="p-2 mono whitespace-nowrap">{fmt(r.ts)}</td>
                                <td className="p-2">
                                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.kind === "login" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`}>
                                        {r.kind}
                                    </span>
                                </td>
                                <td className="p-2 mono truncate max-w-[220px]" title={r.email}>{r.email || "—"}</td>
                                <td className="p-2">{r.role}</td>
                                <td className="p-2 mono">{r.ip || "—"}</td>
                                <td className="p-2 mono">
                                    {r.duration_seconds != null ? `${Math.round(r.duration_seconds / 60)}m` : "—"}
                                </td>
                                <td className="p-2 truncate max-w-[240px] text-muted-foreground hidden md:table-cell" title={r.user_agent}>
                                    {r.user_agent || "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
